import React, { Dispatch, SetStateAction } from 'react';
import { IAvatarSkin } from '@interfaces/avatarSkin';
import {
  IEvent,
  IFollower,
  IPixelStreamingClient,
  PlayerSession,
} from '@interfaces/event';
import { IParticipant, IParticipantRegion } from '@interfaces/participants';
import { IRoom } from '@interfaces/rooms';
import { IRealtimeEntity } from '@interfaces/realtimeEntities';
import type { ILiveBrandingEntity } from '@interfaces/map';
import { IFollowerData } from '@interfaces/followers';
import { IInitMessage } from '@interfaces/streaming';
import { SendToBriefcasePropsTypeFull } from '../../components/DashboardContent/interfaces';
import type { EditorFocus } from '@core/dashboard/components/EditorMode/editorCategories';
import type {
  MapEditorModeState,
  MapLayoutRefusal,
  MapLayoutStandingRoom,
} from '@core/dashboard/components/EditorMode/mapEditorUnreal';
import {
  IMillicastEventTokens,
  IMillicastStreamTokens,
  IMillicastTokens,
} from '@interfaces/millicastTokens';
import type { IBrandingApi } from '@services/BrandingsService';
import { IPointOfInterest } from '@interfaces/pointsOfInterest';
import { IShareMediaParams } from '@interfaces/shareMediaParams';
import { IDynamicMapTrigger } from '@shared/interfaces/dynamicMapTrigger';
import { IMillicastPublisher } from '@interfaces/millicastPublisher';
import { PixelStreaming } from '@tensorworks/libspsfrontend';
import { PixelStreaming as MytaPixelStreamingClient } from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5';
import { IGamecastVideoStats } from '../../components/DashboardContent/GameCast/interfaces';
import { IMytaStreamsContext } from '../../components/DashboardContent/MytaStreams/interfaces';
import {
  DegradedService,
  IEventParticipant,
} from '@interfaces/eventParticipant';
import { IUser } from '@interfaces/user';
import { AnalyticsType } from '@interfaces/analytics';
import {
  IStartStreamSessionOutput,
  StreamSessionStatus,
} from '@interfaces/gamecast';
import ParticipantScopes from '@constants/scopes';
import { NavigationModeType } from '@constants/navigationMode';

export interface EventParticipantDataType {
  currentEvent?: IEvent;
  currentEventParticipant: IEventParticipant | null;
}

export type UpdateParticipantType = (
  participantId: string,
  newParticipantInfo: Partial<IParticipant>,
) => void;

export interface ParticipantsStateHookProps {
  currentParticipantId: string;
  currentEventId: string | undefined;
  currentParticipant: IParticipant | undefined;
  setCurrentParticipant: React.Dispatch<
    React.SetStateAction<IParticipant | undefined>
  >;
  participants: IParticipant[];
  setParticipants: React.Dispatch<React.SetStateAction<IParticipant[]>>;
  currentRoom: IRoom | null;
  setCurrentRoom: React.Dispatch<React.SetStateAction<IRoom | null>>;
  currentRegion: IParticipantRegion | null;
  setCurrentRegion: React.Dispatch<
    React.SetStateAction<IParticipantRegion | null>
  >;
  websocketSessionId?: string;
  setIsParticipantRoomChanged: React.Dispatch<React.SetStateAction<boolean>>;
  rooms: IRoom[];

  trackEventAnalytics: (type: AnalyticsType, payload: any) => void;
}

export type SetParticipantStateType = (
  participantId: string,
  data: {
    eventId?: string | null;
    roomId?: string | null;
    region?: IParticipantRegion | null;
    regions?: IParticipantRegion[];
    gameSessionId?: string | null;
    isDemoUser?: boolean;
    handRaised?: boolean;
    handRaisedAt?: number;
    isSeatRegion?: boolean;
  },
) => void;

export type NewRegionsType = {
  newRegion: IParticipantRegion | null;
  newRegions: IParticipantRegion[];
};

export enum SendMutedStateMessageEnum {
  MUTE = 'MUTE',
  UNMUTE = 'UNMUTE',
}

export enum InitMessageActions {
  INIT = 'INIT',
}

export enum DeviceTypeEnum {
  // Phone Types
  Phone = 'Phone',
  PhoneIOS = 'IOSPhone',
  PhoneAndroid = 'AndroidPhone',
  PhoneWin = 'WindowsPhone',

  // Tablet Types
  Tablet = 'Tablet',
  TabletIOS = 'IOSTablet',
  TabletAndroid = 'AndroidTablet',

  // Desktop Types
  Desktop = 'Desktop',
}

export interface UseInitMessageProps {
  token?: string;
  websocketSessionId: string;
  dolbyToken: string;
  currentParticipant?: IParticipant;
  currentEventParticipant?: IEventParticipant | null;
  currentEvent?: IEvent;
  teleportingToRoom: IRoom | null;
  teleportEnabled: boolean;
  user: IUser;
  userPermissions: ParticipantScopes[];
  generateNewGameliftStreamsSessionKey: () => string;
}

export enum StreamingProviders {
  GameCast = 'GAMECAST',
  CoreWeave = 'COREWEAVE',
  MytaStreams = 'MYTA_STREAMS',
}

export interface IMapFollower {
  pending: IFollowerData[];
  accepted: IFollowerData[];
}

export type MapFollowersType = (followers: IFollower[]) => IMapFollower;

export interface IWebsocketMessageFlag {
  flag: boolean;
  timestamp: number;
}

// export interface IRecreateAvaturnAvatar {
//   avatarId: string;
//   avaturnId: string;
//   url: string;
//   gender: Gender;
// }
export interface IUnrealScreenSize {
  width: number;
  height: number;
}

export interface IMytaverseEventContext {
  currentEventId: string;
  currentParticipantId: string;

  setParticipantsSound: (level: number) => void;
  participantsSound: number;
  assistantSound: number;
  setAssistantSound: (level: number) => void;
  enableSoulmachinesAssistant: boolean;
  setEnableSoulmachinesAssistant: React.Dispatch<React.SetStateAction<boolean>>;
  assistantScriptUpdateTick: number;
  setAssistantScriptUpdateTick: React.Dispatch<React.SetStateAction<number>>;
  eventLoaded: boolean;
  setEventLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  setGameSound: (level: number) => void;
  initMessageHandler: () => Promise<IInitMessage | null>;
  gameSound: number;
  rooms: IRoom[];
  reaction: string | null;
  emoji: string | null;
  setEmoji: (emoji: string | null) => void;
  setReaction: (reaction: string | null) => void;
  isHandRaised: boolean;
  setIsHandRaised: React.Dispatch<React.SetStateAction<boolean>>;
  toggleRaiseHand: () => void;
  sharingWindowFullsceen: boolean;
  setSharingWindowFullscreen: () => void;
  handleMinimizeSharingScreen: () => void;
  isMinimizedScreenSharing: boolean;
  setIsMinimizedScreenSharing: React.Dispatch<React.SetStateAction<boolean>>;
  currentEvent: IEvent | undefined;
  setCurrentEvent: React.Dispatch<React.SetStateAction<IEvent | undefined>>;
  currentEventParticipant: IEventParticipant | null;

  currentRoom: IRoom | null | undefined;
  setCurrentRoom: Dispatch<SetStateAction<IRoom | null>>;
  gameCastStreamId: string;
  setGameCastStreamId: Dispatch<SetStateAction<string>>;
  gameCastStreamRegion: string;
  setGameCastStreamRegion: Dispatch<SetStateAction<string>>;
  currentRegion: IParticipantRegion | null;
  setCurrentRegion: (region: IParticipantRegion | null) => void;
  gameCastStreamRequestSended: boolean;
  setGameCastStreamRequestSended: React.Dispatch<React.SetStateAction<boolean>>;
  participants: IParticipant[];
  setParticipants: React.Dispatch<React.SetStateAction<IParticipant[]>>;
  currentParticipant: IParticipant | undefined;
  setCurrentParticipant: React.Dispatch<
    React.SetStateAction<IParticipant | undefined>
  >;
  setCurrentEventParticipant: React.Dispatch<
    React.SetStateAction<IEventParticipant | null>
  >;
  shareMillicastVideoWithSound: boolean;
  setShareMillicastVideoWithSound: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  teleportEnabled: boolean;
  teleportingToRoom: IRoom | null;
  setTeleportingToRoom: Dispatch<SetStateAction<IRoom | null>>;
  teleportToRoom: (room: IRoom | null) => void;
  isTeleportingToRoomByUnreal: boolean;
  setIsTeleportingToRoomByUnreal: Dispatch<SetStateAction<boolean>>;

  teleportToParticipant: (participant: IParticipant | null) => Promise<void>;
  currentRoomDolbySpatialAudioScale: number;
  setRoomDolbySpatialAudioScale: (
    roomId: string,
    dolbySpatialAudioScale: number,
  ) => void;
  // Applies the CMS edit of a room's assistant fallback identity to both
  // `rooms` and `currentRoom`; see the provider for why both.
  setRoomAssistantIdentity: (
    roomId: string,
    patch: Pick<IRoom, 'assistantIcon' | 'assistantName'>,
  ) => void;
  updateRoomScale: (value: number) => void;
  previousSkin: IAvatarSkin | null;
  ue5WebsocketConnected: boolean;
  setUe5WebsocketConnected: React.Dispatch<React.SetStateAction<boolean>>;
  selectSkin: (skin: IAvatarSkin) => Promise<void>;
  loading: boolean;
  isTeleporting: boolean;
  setIsTeleporting: React.Dispatch<React.SetStateAction<boolean>>;
  teleportFinished: boolean;
  setTeleportFinished: React.Dispatch<React.SetStateAction<boolean>>;
  openAdminSettingsModal: boolean;
  setOpenAdminSettingsModal: React.Dispatch<React.SetStateAction<boolean>>;

  muted: boolean;
  poiPreviewSrc: SendToBriefcasePropsTypeFull | null;
  setPoiPreviewSrc: React.Dispatch<
    React.SetStateAction<SendToBriefcasePropsTypeFull | null>
  >;
  setMuted: (value?: boolean) => void;
  dolbyToken: string;
  streamChatToken: string;
  /** Services the backend reported as unavailable (MV-1856). */
  unavailableServices: DegradedService[];
  streamChatMemberId: string;
  millicastTokens: IMillicastEventTokens | null;
  getMillicastStreamTokens: (
    screenName: string,
  ) => Promise<IMillicastStreamTokens | null>;
  streamingProvider: StreamingProviders | null;
  setStreamingProvider: Dispatch<SetStateAction<StreamingProviders | null>>;
  isNativeProviderCheckPending: boolean;
  billingInvoiceId: string | null;
  setBillingInvoiceId: Dispatch<SetStateAction<string | null>>;
  handleBillingStale: (staleInvoiceId?: string) => void;
  mytaStreams: IMytaStreamsContext;
  pointsOfInterest: IPointOfInterest[] | null;
  resetPreviousSkin: () => void;
  userFiles: IPointOfInterest[];
  setUserFiles: React.Dispatch<React.SetStateAction<IPointOfInterest[]>>;
  micUsingByMillicast: boolean;
  setMicUsingByMillicast: React.Dispatch<React.SetStateAction<boolean>>;
  setParticipantState: SetParticipantStateType;
  updateParticipant: UpdateParticipantType;
  recomputeUserEventPermissions: (role: string) => void;
  isDroneMode: boolean;
  setIsDroneMode: React.Dispatch<React.SetStateAction<boolean>>;
  selectNavigationMode: (mode: NavigationModeType) => Promise<void>;

  isShareScreenModalOpen: boolean;
  openShareScreenModal: (params: IShareMediaParams) => Promise<void>;
  closeShareScreenModal: (unlockScreen?: boolean) => Promise<void>;

  openMillicastScreenBroadcastingDataModal: boolean;
  setOpenMillicastScreenBroadcastingDataModal: React.Dispatch<
    React.SetStateAction<boolean>
  >;

  loadingShareScreenModal: boolean;
  setLoadingShareScreenModal: React.Dispatch<React.SetStateAction<boolean>>;

  isShareVideoModalOpen: boolean;
  openShareVideoModal: (params: IShareMediaParams) => Promise<void>;
  closeShareVideoModal: (unlockScreen?: boolean) => Promise<void>;

  loadingShareVideoModal: boolean;
  setLoadingShareVideoModal: React.Dispatch<React.SetStateAction<boolean>>;
  canSpeak: boolean;
  canShowCamera: boolean;
  canShareCamera: boolean;
  canShareScreen: boolean;
  shouldConnectToConference: boolean;
  shareMediaParams?: IShareMediaParams | null;
  setShareMediaParams: React.Dispatch<
    React.SetStateAction<IShareMediaParams | null>
  >;
  gamecastStats: React.MutableRefObject<IGamecastVideoStats | null>;
  showGamecastStats: boolean;
  setShowGamecastStats: React.Dispatch<React.SetStateAction<boolean>>;
  showControls: boolean;
  setShowControls: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentSkin: React.Dispatch<React.SetStateAction<IAvatarSkin | null>>;
  currentSkin: IAvatarSkin | null;

  initMessageSended: boolean;
  openLeaveRegionDialog: boolean;
  setOpenLeaveRegionDialog: React.Dispatch<React.SetStateAction<boolean>>;
  openCameraPublisherDialog: boolean;
  setOpenCameraPublisherDialog: React.Dispatch<React.SetStateAction<boolean>>;
  openTeleportToRoomDialog: boolean;
  setOpenTeleportToRoomDialog: React.Dispatch<React.SetStateAction<boolean>>;
  selectedTeleportRoom: IRoom | null;
  setSelectedTeleportRoom: React.Dispatch<React.SetStateAction<IRoom | null>>;
  initScreenMediaStream: (force?: boolean) => Promise<void>;
  handleStopScreenStream: (streamId: string) => Promise<void>;
  setInitMessageSended: React.Dispatch<React.SetStateAction<boolean>>;
  isStreamLoading: boolean;
  isStreamLoaded: boolean;
  setIsStreamLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsStreamLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  audioRef: React.MutableRefObject<HTMLAudioElement | null>;
  //streamSession: GameCastStreamSession | null;
  shareVideoPublishers: IMillicastPublisher | null;
  setShareVideoPublishers: (publisher: IMillicastPublisher | null) => void;

  shareVideoMediaStream: MediaStream | null;
  setShareVideoMediaStream: React.Dispatch<
    React.SetStateAction<MediaStream | null>
  >;

  shareScreenPublishers?: IMillicastPublisher[];
  setShareScreenPublishers: (publisher: any) => void;
  developersTerminalMessages: string[];
  setDevelopersTerminalMessages: React.Dispatch<React.SetStateAction<string[]>>;
  openDevelopersTerminal: boolean;
  setOpenDevelopersTerminal: React.Dispatch<React.SetStateAction<boolean>>;
  shareScreenMediaStreams?: MediaStream[];
  setShareScreenMediaStreams: React.Dispatch<
    React.SetStateAction<MediaStream[]>
  >;

  resetPreviousCustomSkin: () => void;
  previousCustomAvatar: string | null;
  startShareVideo: (
    shareVideoPublishers: any,
    shareVideoTokens: IMillicastTokens,
    muted: boolean,
  ) => void;
  stopShareVideo: () => void;

  startShareScreen: (
    shareScreenPublishers: IMillicastPublisher,
    shareScreenTokens: IMillicastTokens,
    muted: boolean,
  ) => void;
  stopShareScreen: (streamId: string) => void;
  cleanStates: () => void;

  trackEventAnalytics: (type: AnalyticsType, payload?: any) => void;

  pendingFollowersData: IFollowerData[];
  setPendingFollowersData: Dispatch<SetStateAction<IFollowerData[]>>;
  acceptedFollowersData: IFollowerData[];
  setAcceptedFollowersData: Dispatch<SetStateAction<IFollowerData[]>>;

  handleMillicastWSMessage: (lastJSONMessage: any) => void;

  gameReadyToPlay: boolean;
  setGameReadyToPlay: Dispatch<SetStateAction<boolean>>;

  ue5CoreWeaveDisabled: boolean;
  setUe5CoreWeaveDisabled: Dispatch<SetStateAction<boolean>>;
  pixelStreamingClient: IPixelStreamingClient | null;
  events: IEvent[];
  webSocketAddress: string | null;
  pixelStreaming: PixelStreaming | null;
  mytaPixelStreaming: MytaPixelStreamingClient | null;
  setMytaPixelStreaming: React.Dispatch<
    React.SetStateAction<MytaPixelStreamingClient | null>
  >;
  coreweaveLoading: boolean;
  dataChannelOpened: boolean;
  playStream: boolean;
  connectedPod: string;
  rootElement: HTMLElement | null;
  setRootElement: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  setPixelStreaming: React.Dispatch<
    React.SetStateAction<PixelStreaming | null>
  >;
  setWebSocketAddress: React.Dispatch<React.SetStateAction<string | null>>;
  togglePlayStream: () => void;
  handleInitializeButton: () => void;
  isPlayButtonPressed: boolean;
  isGamecastCrash: boolean;
  setIsGamecastCrash: React.Dispatch<React.SetStateAction<boolean>>;
  playerSession: PlayerSession | null;
  setPlayerSession: React.Dispatch<React.SetStateAction<PlayerSession | null>>;
  showAvaturnModal: boolean;
  setShowAvaturnModal: React.Dispatch<React.SetStateAction<boolean>>;
  // recreateAvaturnAvatar: IRecreateAvaturnAvatar | null;
  // setRecreateAvaturnAvatar: React.Dispatch<
  //   React.SetStateAction<IRecreateAvaturnAvatar | null>
  // >;
  showExitButton: IWebsocketMessageFlag;
  setShowExitButton: Dispatch<SetStateAction<IWebsocketMessageFlag>>;
  isHideWebUI: IWebsocketMessageFlag;
  isHideWebUIExceptAssistant: IWebsocketMessageFlag;
  isHideAllExceptTopBar: IWebsocketMessageFlag;
  setIsHideWebUI: Dispatch<SetStateAction<IWebsocketMessageFlag>>;
  setIsHideWebUIExceptAssistant: Dispatch<
    SetStateAction<IWebsocketMessageFlag>
  >;
  setIsHideAllExceptTopBar: Dispatch<SetStateAction<IWebsocketMessageFlag>>;
  isUEModalActionInProgress: boolean;
  setIsUEModalActionInProgress: Dispatch<SetStateAction<boolean>>;
  screenshotDownloadUrl: string | null;
  setScreenshotDownloadUrl: Dispatch<SetStateAction<string | null>>;
  fallbackScreenshotDownloadUrlRef: React.MutableRefObject<string | null>;
  processActiveStreamSession: (
    gamecastStreamSession: IStartStreamSessionOutput,
  ) => Promise<{ isStarted: boolean; isAborted: boolean }>;
  gamecastClientRef: any;
  sendMytaverseActions: (currentEventParticipant: IEventParticipant) => void;
  gameliftStreamsSessionKey: string;
  generateNewGameliftStreamsSessionKey: () => string;
  playVideoUrl: string | null;
  handleGamecastStreamSession: ({
    status,
    gamecastStreamSession,
    sessionKey,
  }: {
    status: StreamSessionStatus;
    gamecastStreamSession: IStartStreamSessionOutput | null;
    sessionKey?: string | undefined;
  }) => Promise<void>;
  setPlayVideoUrl: React.Dispatch<React.SetStateAction<string | null>>;

  showExternalKeyboardOverlay: boolean;
  setShowExternalKeyboardOverlay: Dispatch<SetStateAction<boolean>>;

  externalKeyboardConnected: boolean;
  setExternalKeyboardConnected: Dispatch<SetStateAction<boolean>>;

  isUserEnteringEvent: boolean;
  setIsUserEnteringEvent: Dispatch<SetStateAction<boolean>>;

  showCustomSpotsEditor: boolean;
  setShowCustomSpotsEditor: Dispatch<SetStateAction<boolean>>;
  showCustomizeSpotModal: boolean;
  setShowCustomizeSpotModal: Dispatch<SetStateAction<boolean>>;
  customisationItemSelectedTick: number;
  setCustomisationItemSelectedTick: Dispatch<SetStateAction<number>>;
  selectedCustomizationTag: string | null;
  setSelectedCustomizationTag: Dispatch<SetStateAction<string | null>>;
  roomSpots: any[];
  setRoomSpots: Dispatch<SetStateAction<any[]>>;
  selectedCustomizationUeId: string | null;
  setSelectedCustomizationUeId: Dispatch<SetStateAction<string | null>>;
  selectedCustomizationTargetSize: IUnrealScreenSize | null;
  setSelectedCustomizationTargetSize: Dispatch<
    SetStateAction<IUnrealScreenSize | null>
  >;
  selectedCustomizationSpotType:
    | 'IMAGE'
    | 'VIDEO'
    | 'VIDEO_STATIC'
    | 'QR'
    | null;
  setSelectedCustomizationSpotType: Dispatch<
    SetStateAction<'IMAGE' | 'VIDEO' | 'VIDEO_STATIC' | 'QR' | null>
  >;
  /** Bumped on each UE spot click so EditorMode re-opens the inspector for the same id. */
  customizationSelectionRevision: number;
  setCustomizationSelectionRevision: Dispatch<SetStateAction<number>>;
  isBrandingModeEnabled: boolean;
  setIsBrandingModeEnabled: Dispatch<SetStateAction<boolean>>;

  // Cross-tree signal so EditorMode (rendered in HomeBottomBar) can ask
  // AdminTools (rendered in DashboardTopBar) to open the branding library or
  // the create-branding flow. AdminTools owns the modals; it consumes the
  // request and resets the flag back to null.
  brandingActionRequest: 'library' | 'create' | null;
  setBrandingActionRequest: Dispatch<
    SetStateAction<'library' | 'create' | null>
  >;

  // Which "focus" (editor tab) is active. The single source of truth: it is
  // written both from outside the editor — EditAreaPickerModal, which picks a
  // section for an already-open editor as well as a closed one — and from the
  // tab strip inside it, and EditorModeContext's `activeFocus` is a projection
  // of it. Kept here rather than in EditorModeContext because that provider is
  // unmounted with the editor overlay, so a value local to it could not be set
  // by the picker while the editor was open (MV-1912).
  brandingEditorFocus: EditorFocus;
  setBrandingEditorFocus: Dispatch<SetStateAction<EditorFocus>>;

  // True while the editor inspector is rendering content (selectedNode !== null
  // in EditorModeContext). The Assistant UI lives outside the editor tree and
  // reads this flag to hide Noa when the inspector occupies the same screen
  // area. The inspector also exposes a button to clear selection and re-open
  // the assistant via `requestAssistantOpen`.
  isEditorInspectorOpen: boolean;
  setIsEditorInspectorOpen: Dispatch<SetStateAction<boolean>>;

  // Incrementing nonce — bumped when the editor's "Open virtual assistant"
  // button is clicked. AssistantProvider watches this to call setOpen(true)
  // without a direct context dependency on the editor.
  assistantOpenRequestNonce: number;
  requestAssistantOpen: () => void;

  // Same cross-boundary pattern as assistantOpenRequestNonce above: the editor
  // (e.g. Editor Mode's "Teleport to trigger") lives outside AssistantProvider,
  // so it can't call useAssistant().triggerRegionChange directly. It calls
  // requestAssistantRegionChange(trigger, hasContent) instead; AssistantProvider
  // watches pendingAssistantRegionTrigger's nonce and replays the region
  // change, force-opening only when hasContent is true.
  pendingAssistantRegionTrigger: {
    trigger: string;
    nonce: number;
    hasContent: boolean;
  } | null;
  requestAssistantRegionChange: (trigger: string, hasContent: boolean) => void;

  // Teleporting to a spot (Inspector's "teleport to spot") makes Unreal echo
  // back a CUSTOMISATION_ITEM_SELECTED message for that same spot shortly
  // after, which would otherwise re-select it and reopen the Inspector pane
  // right after code intentionally closed it. Set this to the spot's ueId
  // right before teleporting; UESelectionBridge checks it and skips exactly
  // one matching re-selection, then clears it.
  suppressedCustomizationSelectIdRef: React.MutableRefObject<string | null>;
  suppressNextCustomizationSelect: (ueId: string) => void;

  currentBrandingId: string | null;
  setCurrentBrandingId: Dispatch<SetStateAction<string | null>>;

  brandingSpots: any[];
  setBrandingSpots: Dispatch<SetStateAction<any[]>>;

  brandingHotspots: any[];
  setBrandingHotspots: Dispatch<SetStateAction<any[]>>;

  // The branding record behind `currentBrandingId ?? room.activeBrandingId`.
  // Read for the presentation values stored on it (assistant icon and name).
  currentBranding: IBrandingApi | null;
  setCurrentBranding: Dispatch<SetStateAction<IBrandingApi | null>>;

  pendingEditorHotspotSelection: { hotspotId: string; nonce: number } | null;
  setPendingEditorHotspotSelection: Dispatch<
    SetStateAction<{ hotspotId: string; nonce: number } | null>
  >;
  requestEditorHotspotSelection: (hotspotId: string) => void;

  currentGeometryRooms: MapLayoutStandingRoom[] | null;
  setCurrentGeometryRooms: Dispatch<
    SetStateAction<MapLayoutStandingRoom[] | null>
  >;

  currentGeometryRefusals: MapLayoutRefusal[] | null;
  setCurrentGeometryRefusals: Dispatch<
    SetStateAction<MapLayoutRefusal[] | null>
  >;

  // The lobby the standing layout opens in, out of the same report. Null when it opens on the
  // entrance the host map bakes in, which is every layout that has never named one.
  //
  // Held apart from `currentGeometryRooms` because it is not one of them: the room list is the
  // author's chain and the lobby is chosen beside it, so folding the start row into that array is
  // exactly the mistake the reader drops it to avoid.
  //
  // The whole standing room rather than its id, because the entrance carries content: its spots and
  // its AI regions arrive on this row and nowhere else, so a reader that kept only the id would
  // leave the one room every visitor is guaranteed to stand in unaddressable. See
  // parseGeometryStartRoom.
  currentGeometryStartRoom: MapLayoutStandingRoom | null;
  setCurrentGeometryStartRoom: Dispatch<
    SetStateAction<MapLayoutStandingRoom | null>
  >;
  // Brandable objects Unreal reports standing in the world right now
  // (BRANDING_ENTITIES). Held here rather than in the editor's own provider for
  // the same reason the geometry report is: both the writer and one of the
  // readers sit outside `MapEditorProvider` altogether. The websocket handler
  // that writes it lives in `DashboardContent`'s hooks, which `MapEditorContext`
  // does not reach, and `AdminTools` in the top bar reads it to decide whether
  // the Objects focus may be offered.
  //
  // Null before the first report — "UE has not said" — as against an empty
  // array, which is UE saying there is nothing standing. Each report replaces
  // the list whole; it is never merged into.
  liveBrandingEntities: ILiveBrandingEntity[] | null;
  setLiveBrandingEntities: Dispatch<
    SetStateAction<ILiveBrandingEntity[] | null>
  >;

  // Rows of the local draft Unreal could not place (LAYOUT_PREVIEW_REFUSED). Null before any
  // draft has been previewed in this session.
  previewRefusals: MapLayoutRefusal[] | null;
  setPreviewRefusals: Dispatch<SetStateAction<MapLayoutRefusal[] | null>>;

  editorModeState: MapEditorModeState | null;
  setEditorModeState: Dispatch<SetStateAction<MapEditorModeState | null>>;

  pendingHotspotPlacement: {
    brandingHotspotId: string;
    isCreated: boolean;
  } | null;
  setPendingHotspotPlacement: Dispatch<
    SetStateAction<{
      brandingHotspotId: string;
      isCreated: boolean;
    } | null>
  >;
  hotspotLiveTransform: {
    brandingHotspotId: string;
    position?: number[];
    rotation?: number[];
    scale?: number | number[];
    nonce: number;
  } | null;
  publishHotspotLiveTransform: (update: {
    brandingHotspotId: string;
    position?: number[];
    rotation?: number[];
    scale?: number | number[];
  }) => void;

  // Entities spawned live over the WebSocket. Live state is per-session — the
  // engine keeps nothing across a level change — but what an author saved does
  // persist, on the branding, so the list is cleared with the room and then
  // reloaded from there by the editor.
  realtimeEntities: IRealtimeEntity[];
  upsertRealtimeEntity: (
    update: Partial<IRealtimeEntity> & { entityId: string },
  ) => void;
  removeRealtimeEntity: (entityId: string) => void;
  clearRealtimeEntities: () => void;
  // Loads a branding's saved set. Admin-scoped, so only the editor layer calls it.
  reloadRealtimeEntities: (brandingId: string | null) => Promise<void>;
  bumpRealtimeEntitiesRevision: () => void;
  // Bumped whenever the registry was reset or another admin changed the saved
  // set — an open editor reloads on the change, a closed one reads nothing.
  realtimeEntitiesRevision: number;
  // The engine's settled gizmo-drag report (inbound UPDATE_REALTIME_ENTITY),
  // republished for the open inspector — the same shape and nonce discipline
  // as hotspotLiveTransform above.
  realtimeEntityLiveTransform: {
    entityId: string;
    position?: number[];
    rotation?: number[];
    scale?: number[];
    nonce: number;
  } | null;
  publishRealtimeEntityLiveTransform: (update: {
    entityId: string;
    position?: number[];
    rotation?: number[];
    scale?: number[];
  }) => void;

  familiarizeState: null | { name: string; payload?: any };
  setFamiliariseState: Dispatch<
    SetStateAction<null | { name: string; payload?: any }>
  >;

  resetKeyboardPressedKeys: () => void;

  aiTriggerName: string;
  setAiTriggerName: Dispatch<SetStateAction<string>>;

  // AI triggers reported by dynamically-spawned level chunks — no
  // corresponding static region in the map's MapTemplate. Fetched once per
  // map and appended to optimistically after a successful registration; see
  // DashboardContent/hooks/index.ts (registration) and useRegionIndex.ts
  // (merged into the scriptable-trigger list).
  dynamicAiTriggers: IDynamicMapTrigger[];
  setDynamicAiTriggers: Dispatch<SetStateAction<IDynamicMapTrigger[]>>;

  objectPoiId: string | null;
  setObjectPoiId: Dispatch<SetStateAction<string | null>>;

  spotFullscreenViewId: string | null;
  setSpotFullscreenViewId: Dispatch<SetStateAction<string | null>>;

  // The AI trigger (region) that owns the clicked spot, sent by UE alongside
  // SPOT_FULLSCREEN_VIEW (`aiTrigger`). Lets the assistant resolve the clicked
  // spot's script by the spot's OWNING region rather than the avatar's current
  // live region — clicking a spot in Expo #2 while standing in Expo #1 must
  // play Expo #2's script.
  spotFullscreenViewTrigger: string | null;
  setSpotFullscreenViewTrigger: Dispatch<SetStateAction<string | null>>;

  // Where the in-world clip was playing when the spot was clicked, in SECONDS
  // (fractional), so the overlay resumes the clip instead of restarting it.
  // UE always sends it on SPOT_FULLSCREEN_VIEW — an image spot, or a video that
  // has not started, sends 0, meaning "from the beginning". Same unit as
  // HTMLMediaElement.currentTime, so it needs no conversion in either direction.
  spotFullscreenViewStartAt: number;
  setSpotFullscreenViewStartAt: Dispatch<SetStateAction<number>>;
}
